import { usePaginatedList, type PaginatedListProps } from 'components/forms/inputs/rhf-inputs/usePaginatedList';
import { GLOBAL_SEARCH_ENTITY_ICON } from 'components/global-search/entityDisplay';
import { splitHighlightRuns } from 'components/global-search/HighlightedText';
import { SearchIcon } from 'components/icons';
import {
    Combobox,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
} from 'components/ui/combobox';
import { Drawer, DrawerContent, DrawerSwipeHandle, DrawerTitle, DrawerTrigger } from 'components/ui/drawer';
import { Input } from 'components/ui/input';
import { InputGroupAddon } from 'components/ui/input-group';
import { FOCUS_RING_INSET } from 'layout/NewDesignLayout/focusRing';
import React, { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts';
import { useLazyGetMinGroupsListWithFiltersQuery } from 'store/api/groups';
import { useLazyGetMattersListQuery } from 'store/api/matters';
import { useGetCurrentTenantQuery } from 'store/api/tenants';
import { ContactMinDto } from 'types/contacts';
import { FeatureFlag } from 'types/features';
import { PartyGroupMin } from 'types/groups';
import { MatterListItemMin } from 'types/matters';
import { cn } from 'utils/cn';
import { ENTITY_TYPE_FAMILY, familyTileClass } from 'utils/families';
import { getClassifierText, t } from 'utils/getTranslations';
import { getInitials } from 'utils/nameOrder';
import { getClientNumber, isPartyNumberShownFor } from 'utils/partyNameUtil';
import { highlightSearchTerm } from 'utils/searchTermHighlight';
import { useClassifiers } from 'utils/useClassifiers';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { PlaceholderInputType } from '../samplePrompts';

/** A record picked into a template slot: what its chip in the composer needs. */
export interface SlotRecord {
    entityType: 'PARTY' | 'MATTER' | 'PARTY_GROUP';
    id: number;
    label: string;
    /** A contact's number, where the firm shows numbers for its relationship type. */
    partyNumber?: string | null;
}

interface PlaceholderAutocompleteProps {
    inputType: PlaceholderInputType;
    placeholder: string;
    /** The phone's form: a sheet rising from the composer instead of a menu over it. */
    asSheet?: boolean;
    /** The search text, which fills the slot as typed until a record is picked. */
    onChange: (value: string) => void;
    onPick: (record: SlotRecord) => void;
}

interface Row {
    record: SlotRecord;
    /** The line under the name: a contact's relationship type, a case's client. */
    detail?: string;
    /** Why the record cannot be picked; set, the row is disabled and the line under the name says it. */
    disabledReason?: string;
}

const RELATIONSHIP_TYPE = ['RELATIONSHIP_TYPE'];

const PICKER_PAGE_SIZE = 20;

/**
 * A slot's popup anchor: the slot's span on the composer's top edge, so the popup opens above the composer, never over
 * the prompt being filled when the slot sits on a later line of it.
 */
export const aboveComposer = (element: Element | null) => {
    if (!element) return null;
    return {
        contextElement: element,
        getBoundingClientRect: () => {
            const { x, top, width } = element.getBoundingClientRect();
            const composerTop = element.closest('[data-ai-composer]')?.getBoundingClientRect().top ?? top;
            return new DOMRect(x, composerTop, width, 0);
        },
    };
};

function RecordTile({ record }: { record: SlotRecord }) {
    const Icon = GLOBAL_SEARCH_ENTITY_ICON[record.entityType];
    return (
        <span
            aria-hidden
            className={cn(
                'text-caption grid size-8 shrink-0 place-items-center rounded-[9px] font-semibold',
                familyTileClass(ENTITY_TYPE_FAMILY[record.entityType])
            )}
        >
            {record.entityType === 'PARTY' ? getInitials(record.label) : <Icon className="size-4" />}
        </span>
    );
}

/** The row's name with the part the search matched in semibold, matched as the search does: case- and accent-blind. */
function RowLabel({ row, search }: { row: Row; search: string }) {
    const { partyNumber, label } = row.record;
    return (
        <span className="flex min-w-0 flex-col text-start">
            <span className="text-meta text-foreground truncate">
                {partyNumber ? <span className="tabular-nums">[{partyNumber}] </span> : null}
                {splitHighlightRuns(highlightSearchTerm(label, search)).map((run, index) =>
                    run.highlighted ? (
                        // oxlint-disable-next-line react-doctor/no-array-index-as-key -- the runs of one label, never reordered
                        <b key={index} className="font-semibold">
                            {run.text}
                        </b>
                    ) : (
                        run.text
                    )
                )}
            </span>
            {row.disabledReason ? (
                <span className="text-caption text-foreground-tertiary">{row.disabledReason}</span>
            ) : row.detail ? (
                <span className="text-caption text-foreground-tertiary truncate">{row.detail}</span>
            ) : null}
        </span>
    );
}

interface PickerProps extends PlaceholderAutocompleteProps {
    rows: Row[];
    isLoading: boolean;
    onSearch: (value: string) => void;
}

/** The desktop picker: the slot is the search field, its matches a menu opening upward from it. */
function MenuPicker({ placeholder, rows, isLoading, onSearch, onChange, onPick }: PickerProps) {
    const intl = useIntl();
    const [search, setSearch] = useState('');
    const field = useRef<HTMLInputElement>(null);
    return (
        <Combobox<Row>
            items={rows}
            filter={null}
            autoHighlight
            defaultOpen
            itemToStringLabel={(row) => row.record.label}
            onInputValueChange={(value) => {
                setSearch(value);
                onSearch(value);
                onChange(value);
            }}
            onValueChange={(row) => {
                if (row) onPick(row.record);
            }}
        >
            <ComboboxInput
                ref={field}
                autoFocus
                showTrigger={false}
                aria-label={placeholder}
                placeholder={placeholder}
                className="border-ring bg-card text-meta shadow-focus-ring inline-flex h-6 w-56 max-w-full rounded-md align-middle [&_input]:h-6 [&_input]:px-0"
            >
                <InputGroupAddon align="inline-start" className="py-0 ps-1.5 pe-1">
                    <SearchIcon className="text-foreground-tertiary size-3.5" />
                </InputGroupAddon>
            </ComboboxInput>
            <ComboboxContent
                anchor={() => aboveComposer(field.current)}
                side="top"
                className="border-border-menu bg-popover shadow-menu w-64 rounded-[12px] border p-0"
            >
                <ComboboxEmpty className="text-meta text-muted-foreground px-3 py-2">
                    {intl.formatMessage({ id: isLoading ? 'loading' : 'no-options' })}
                </ComboboxEmpty>
                <ComboboxList className="flex flex-col gap-px p-1.5">
                    {(row: Row) => (
                        <ComboboxItem
                            key={row.record.id}
                            value={row}
                            disabled={row.disabledReason != null}
                            className="data-highlighted:bg-primary-wash gap-2.5 rounded-sm px-2 py-1.5 pr-2"
                        >
                            <RecordTile record={row.record} />
                            <RowLabel row={row} search={search} />
                        </ComboboxItem>
                    )}
                </ComboboxList>
            </ComboboxContent>
        </Combobox>
    );
}

/**
 * The phone's picker: the kit Drawer as a sheet on the chat, flush with the composer, opened with the slot and
 * reopened from its pill. The dim covers the chat above the composer only, so sheet and composer read as one surface.
 */
function SheetPicker({ inputType, placeholder, rows, isLoading, onSearch, onChange, onPick }: PickerProps) {
    const intl = useIntl();
    const [open, setOpen] = useState(true);
    const [search, setSearch] = useState('');

    return (
        <Drawer open={open} onOpenChange={setOpen}>
            <DrawerTrigger className="border-border-dashed bg-surface-subtle text-meta text-foreground-secondary hit-area relative inline-flex h-6 items-center gap-1 rounded-md border border-dashed px-1.5 align-middle font-medium">
                {search || placeholder}
            </DrawerTrigger>
            {/* The kit places a downward sheet with a `data-[swipe-direction=down]:` rule, and sizes its backdrop
                `min-h-dvh`: both are overridden at that weight, so the sheet and its dim stop at the composer. Its
                bleed below the sheet would cover the composer's first line: the two are one white surface already. */}
            <DrawerContent
                layerClassName="bottom-(--ai-composer-height,0px) min-h-0"
                className="bg-card px-3 pt-2 pb-3 shadow-none after:hidden data-[swipe-axis=y]:[--drawer-content-max-height:calc(100dvh-6rem-var(--ai-composer-height,0px))] data-[swipe-direction=down]:bottom-(--ai-composer-height,0px) data-[swipe-direction=down]:rounded-t-[20px] data-[swipe-direction=down]:border-t-0"
            >
                <div className="flex flex-col gap-2.5 pt-0.5 pb-1.5">
                    <DrawerSwipeHandle className="after:bg-border group-data-[swipe-axis=y]/drawer-popup:h-1 group-data-[swipe-axis=y]/drawer-popup:after:h-1 group-data-[swipe-axis=y]/drawer-popup:after:w-9" />
                    <DrawerTitle className="text-label text-muted-foreground px-2 uppercase">
                        {intl.formatMessage({ id: `ai-chat.sample-prompts.choose-record.${inputType}` })}
                    </DrawerTitle>
                    <Input
                        autoFocus
                        value={search}
                        aria-label={placeholder}
                        placeholder={placeholder}
                        className="text-base"
                        onChange={(event) => {
                            setSearch(event.target.value);
                            onSearch(event.target.value);
                            onChange(event.target.value);
                        }}
                    />
                </div>
                {rows.length === 0 ? (
                    <p role="status" className="text-meta text-muted-foreground m-0 px-2 py-2">
                        {intl.formatMessage({ id: isLoading ? 'loading' : 'no-options' })}
                    </p>
                ) : null}
                <ul
                    aria-busy={isLoading || undefined}
                    className="m-0 flex list-none flex-col gap-px overflow-y-auto p-0"
                >
                    {rows.map((row) => (
                        <li key={row.record.id}>
                            <button
                                type="button"
                                disabled={row.disabledReason != null}
                                onClick={() => {
                                    setOpen(false);
                                    onPick(row.record);
                                }}
                                className={cn(
                                    'hover:bg-row-stripe flex min-h-11 w-full cursor-pointer items-center gap-2.5 rounded-sm px-2 py-1.5 disabled:pointer-events-none disabled:opacity-50',
                                    FOCUS_RING_INSET
                                )}
                            >
                                <RecordTile record={row.record} />
                                <RowLabel row={row} search={search} />
                            </button>
                        </li>
                    ))}
                </ul>
            </DrawerContent>
        </Drawer>
    );
}

function PickerFor<TOption>({
    paginated,
    toRows,
    ...props
}: PlaceholderAutocompleteProps & {
    paginated: PaginatedListProps<TOption>;
    toRows: (options: TOption[]) => Row[];
}) {
    const list = usePaginatedList<TOption>({ pageSize: PICKER_PAGE_SIZE, ...paginated });
    const rows = toRows(list.dataToDisplay);
    const Picker = props.asSheet ? SheetPicker : MenuPicker;
    return (
        <Picker
            {...props}
            rows={rows}
            isLoading={list.isLoading || list.isAwaitingFirstPage}
            onSearch={list.handleSearchChange}
        />
    );
}

function ContactPicker(props: PlaceholderAutocompleteProps) {
    const { classifiers } = useClassifiers({ codes: RELATIONSHIP_TYPE });
    const isPartyNumberUsageEnabled = useIsFeatureEnabled(FeatureFlag.PARTY_NUMBER_USAGE);
    const { data: tenant } = useGetCurrentTenantQuery();
    return (
        <PickerFor<ContactMinDto>
            {...props}
            paginated={{ fetch: useLazyGetMinContactsListWithFiltersQuery, searchField: ['name', 'partyNumber'] }}
            toRows={(contacts) =>
                contacts.map((contact) => ({
                    record: {
                        entityType: 'PARTY',
                        id: contact.id,
                        label: contact.name,
                        partyNumber: isPartyNumberShownFor(tenant, contact.relationshipTypeCode)
                            ? getClientNumber(contact, isPartyNumberUsageEnabled)
                            : null,
                    },
                    detail:
                        getClassifierText(classifiers['RELATIONSHIP_TYPE'], contact.relationshipTypeCode) || undefined,
                }))
            }
        />
    );
}

const PlaceholderAutocomplete: React.FC<PlaceholderAutocompleteProps> = (props) => {
    const { inputType } = props;
    if (inputType === 'contact') {
        return <ContactPicker {...props} />;
    }

    if (inputType === 'group') {
        return (
            <PickerFor<PartyGroupMin>
                {...props}
                paginated={{ fetch: useLazyGetMinGroupsListWithFiltersQuery, searchField: 'groupName' }}
                toRows={(groups) =>
                    groups.map((group) => ({
                        record: { entityType: 'PARTY_GROUP', id: group.id, label: group.groupName },
                    }))
                }
            />
        );
    }

    return (
        <PickerFor<MatterListItemMin>
            {...props}
            paginated={{
                fetch: useLazyGetMattersListQuery,
                searchField: ['fullName', 'clientParty.name'],
                sort: ['clientParty.name,asc', 'name,asc'],
            }}
            toRows={(matters) =>
                matters.map((matter) => ({
                    record: { entityType: 'MATTER', id: matter.id, label: matter.fullName },
                    detail: matter.clientParty?.name,
                    // As the app's case pickers: no case of a client marked persona non grata.
                    disabledReason: matter.clientParty?.isPersonaNonGrata
                        ? t('contacts.tooltips.add-new-case-disabled-persona-non-grata')
                        : undefined,
                }))
            }
        />
    );
};

export default PlaceholderAutocomplete;
